import { Box, Flex, Text, useBreakpointValue } from "@chakra-ui/react";
import React, { useDeferredValue, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import colors from "../utils/colors";
import DesktopHeader from "../components/defaultHeader/DesktopHeader";
import Heading from "../components/typography/Heading";
import BodyText from "../components/typography/Body";
import InfoCard from "../components/cards/InfoCard";
import { peopleOutlineIcon, taskFilledIcon } from "../assets";
import ProfileCard from "../components/cards/ProfileCard";
import CalendarCmp from "../components/Calendar";
import PatientsTable from "../features/patients/components/PatientsTable";
import PatientsUtils from "../features/patients/components/PatientsUtils";
import UpcomingAppsCard from "../components/cards/UpcomingAppsCard";
import { useAppSelector } from "../hooks/useAppSelector";
import NoItemsCard from "../features/patients/components/NoPatientsCard";
import ScheduleAppointmentModal from "../features/appointments/components/ScheduleAppointmentModal";
import AppointmentsHeader from "../features/appointments/components/AppointmentsHeader";
import {
  useGetAppointmentOverview,
  useGetAppointments,
  useGetDashboardOverview,
  useGetPatients,
} from "../apis/doctor";
import { ClipLoader } from "react-spinners";
import AppointmentsBarChart from "../features/appointments/components/AppointmentsBarChart";
import { formalizeAggrData } from "../features/patients/utils";
import CreatePatientModal from "../features/patients/components/CreatePatientModal";
import PatientDetailsModal from "../features/patients/components/PatientDetailsModal";
import { TPatientDetails } from "../types";
import Pagination from "../components/pagination";
import UpdatePatientModal from "../features/patients/components/UpdatePatientModal";

const DoctorHomePage = () => {
  const [patientCategory, setPatientCategory] = useState("");
  const [isCreatePatientModalOpen, setIsCreatePatientModalOpen] =
    useState(false);
  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);
  const [isUpdatePatientModalOpen, setIsUpdatePatientModalOpen] =
    useState(false);
  const [limit, setLimit] = useState(5);
  const [search, setSearch] = useState("");
  const [period, setPeriod] = useState<"year" | "week" | "month">("year");
  const deferedSearch = useDeferredValue(search);
  const [isOpen, setIsOpen] = useState(false);
  const { doctorInfo } = useAppSelector((state) => state.auth);
  const [selectedPatient, setSelectedPatient] = useState({} as TPatientDetails);
  const [currentPage, setCurrentPage] = useState(1);
  const navigate = useNavigate();

  const isMobile = useBreakpointValue({ base: true, lg: false });
  const desktopHeaderHeight = "94px";

  useEffect(() => {
    if (!doctorInfo?._id) {
      navigate("/login");
    }
  }, [doctorInfo, navigate]);

  const { data: patients, isLoading: patientsLoading } = useGetPatients({
    //doctorId: doctorInfo?._id,
    limit,
    page: currentPage,
    riskCategory: patientCategory !== "All" ? patientCategory : "",
    search: deferedSearch,
  });
  const { data: appointments, isLoading: appointmentsLoading } =
    useGetAppointments({
      doctorId: doctorInfo?._id,
      limit: 10000000,
      page: 1,
    });
  const { data: appsOverview, isLoading: appsOverviewLoading } =
    useGetAppointmentOverview({
      doctorId: doctorInfo?._id,
      period,
    });
  const { data: overview, isLoading: overviewLoading } =
    useGetDashboardOverview({
      doctorId: doctorInfo?._id,
    });

  if (!doctorInfo?._id) {
    return (
      <Flex justifyContent={"center"} alignItems={"center"} h="100vh" w="100vw">
        <ClipLoader color={colors.primary} size={"40px"} />
        <Text ml={2}>Loading user information...</Text>
      </Flex>
    );
  }

  if (
    overviewLoading ||
    patientsLoading ||
    appsOverviewLoading ||
    appointmentsLoading
  ) {
    return (
      <Flex justifyContent={"center"} alignItems={"center"} h="100vh" w="100vw">
        <ClipLoader color={colors.primary} size={"40px"} />
      </Flex>
    );
  }

  const informationOnCards = [
    {
      img: peopleOutlineIcon,
      value: overview?.totalPatients ?? 0,
      title: "Total Patients",
    },
    {
      img: peopleOutlineIcon,
      value: overview?.activePatients ?? 0,
      title: "Active Patients",
    },
    {
      img: taskFilledIcon,
      value: overview?.upcomingAppointments?.total ?? 0,
      title: "Upcoming Appts.",
    },
  ];
  const graphData = formalizeAggrData({ data: appsOverview?.data, period });
  const graphToShow = graphData?.map((i: any) => ({
    name: i?.x_axis,
    Appointments: i?.count,
    year: i?.year,
  }));

  return (
    <>
      {!isMobile && (
        <DesktopHeader
          headerContent={
            <Flex direction={"column"} gap={"8px"}>
              <Heading>
                Welcome {doctorInfo?.name?.split(" ")?.[1] || "User"}
              </Heading>
              <BodyText size="s">Let's save some lives today</BodyText>
            </Flex>
          }
          //   position="fixed"
          //   top="0"
          //   left="var(--current-sidebar-width)"
          width="calc(100%)"
          zIndex="1000"
          bg={colors.lightGrey}
        />
      )}

      <Box w="full" pt={!isMobile ? desktopHeaderHeight : "0"}>
        <Flex
          w={"full"}
          gap={{ base: "20px", md: "30px" }}
          direction={{ base: "column", xl: "row" }}
          alignItems="flex-start"
        >
          <Flex
            flex={{ base: "1 1 auto", xl: "0.7" }}
            minWidth={0}
            gap={{ base: "20px", md: "30px" }}
            direction={"column"}
            w="full"
          >
            <Flex
              gap={{ base: "10px", md: "20px" }}
              direction={{ base: "column", sm: "row" }}
              flexWrap="wrap"
            >
              {informationOnCards.map((info, index) => (
                <InfoCard
                  key={index}
                  img={info.img}
                  title={info.title}
                  value={info.value}
                  flex={{ sm: "1" }}
                  borderTopWidth="4px"
                  borderTopColor={colors.primary}
                  borderTopStyle="solid"
                />
              ))}
            </Flex>
            <Box
              backgroundColor={colors.white}
              borderRadius={"8px"}
              w="full"
              p={4}
            >
              <PatientsUtils
                setCategory={setPatientCategory}
                category={patientCategory}
                setSearch={setSearch}
                setPage={setCurrentPage}
                openModal={() => setIsCreatePatientModalOpen(true)}
              />
              {patientsLoading ? (
                <Flex justifyContent="center" alignItems="center" py="100px">
                  <ClipLoader color={colors.primary} size="40px" />
                </Flex>
              ) : patients?.data?.total === 0 ? (
                <NoItemsCard
                  btnText="Add new patients"
                  handleBtnClick={() => setIsCreatePatientModalOpen(true)}
                  subtitle="You do not have any patients yet"
                  title="No patients"
                />
              ) : (
                <>
                  <Box w="100%" overflowX="auto">
                    <PatientsTable
                      setIsDetailsModalOpen={setIsDetailsModalOpen}
                      setIsUpdatePatientModalOpen={setIsUpdatePatientModalOpen}
                      setSelectedPatient={setSelectedPatient}
                      patients={patients?.data?.patients}
                    />
                  </Box>
                  {patients?.data?.total > limit && (
                    <Flex justifyContent="flex-end" mt={4}>
                      <Pagination
                        totalPages={patients.data.lastPage}
                        currentPage={currentPage}
                        onPageChange={setCurrentPage}
                      />
                    </Flex>
                  )}
                </>
              )}
            </Box>
            <Box
              backgroundColor={colors.white}
              borderRadius={"8px"}
              w="full"
              p={4}
            >
              <AppointmentsHeader setPeriod={setPeriod} />
              {appsOverviewLoading ? (
                <Flex justifyContent="center" alignItems="center" py="100px">
                  <ClipLoader color={colors.primary} size="40px" />
                </Flex>
              ) : !appsOverview?.data?.length ? (
                <NoItemsCard
                  btnText="Schedule New Appointment"
                  handleBtnClick={() => setIsOpen(true)}
                  subtitle="You do not have any past appointments"
                  title="No past appointments"
                />
              ) : (
                <AppointmentsBarChart
                  dataKey="Appointments"
                  graphData={graphToShow}
                  xAxis
                  yAxis
                />
              )}
            </Box>
          </Flex>

          <Flex
            direction={"column"}
            gap={{ base: "20px", md: "30px" }}
            flex={{ base: "1 1 auto", xl: "0.3" }}
            minWidth={0}
            w="full"
          >
            <Flex
              direction={{ base: "column", md: "row", xl: "column" }}
              gap={{ base: "20px", md: "15px", xl: "30px" }}
              w="full"
              alignItems={{ md: "stretch" }}
            >
              <Box w="full" flex={{ md: 1, xl: "initial" }}>
                <ProfileCard />
              </Box>
              <Box
                w="full"
                flex={{ md: 1, xl: "initial" }}
                backgroundColor={colors.white}
                p={4}
                borderRadius={"10px"}
              >
                <Text
                  fontWeight={700}
                  fontSize={"16px"}
                  lineHeight={"19.2px"}
                  mb={2}
                >
                  Calendar
                </Text>
                <CalendarCmp />
              </Box>
            </Flex>

            <Box w="full">
              <UpcomingAppsCard
                appointments={appointments?.data?.appointments}
              />
            </Box>
          </Flex>
        </Flex>
      </Box>

      <CreatePatientModal
        isOpen={isCreatePatientModalOpen}
        onClose={() => setIsCreatePatientModalOpen(false)}
        title="Create Patient"
      />
      <PatientDetailsModal
        isOpen={isDetailsModalOpen}
        onClose={() => setIsDetailsModalOpen(false)}
        title="Patient's Details"
        patient={selectedPatient}
      />
      <UpdatePatientModal
        isOpen={isUpdatePatientModalOpen}
        onClose={() => setIsUpdatePatientModalOpen(false)}
        patient={selectedPatient}
        title="Update Patient Info"
      />
      <ScheduleAppointmentModal
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
        title="Schedule New Appointment"
      />
    </>
  );
};

export default DoctorHomePage;
